import { useId, useState, type ReactElement } from "react";
import { ChevronRight } from "lucide-react";
import type { LamaMaturityHierarchyView } from "@playbookai/widget-schemas";
import { TrendIndicator } from "../../atoms/TrendIndicator/TrendIndicator.js";
import { Select } from "../../molecules/Select/Select.js";
import type { WidgetBodyProps } from "../types.js";
import {
  LAMA_SUPPRESSED_BODY,
  LAMA_SUPPRESSED_TITLE,
} from "../lama-report-presentation.js";
import {
  LAMA_HIERARCHY_VIEW_LABEL,
  LAMA_NO_BASELINE_COPY,
  LAMA_NO_RESPONSES_COPY,
  effectiveHierarchyView,
  hierarchyMissingReason,
  hierarchySections,
  lamaChangeDescription,
  lamaPercentLabel,
  type HierarchyCategoryRow,
  type HierarchyQuestionRow,
} from "../lama-hierarchy-presentation.js";
import type { LamaHierarchyView } from "@playbookai/widget-schemas";

const CATEGORY_FILTERS = ["all", "assessed", "unassessed"] as const;
type CategoryFilter = (typeof CATEGORY_FILTERS)[number];

const FILTER_LABEL: Record<
  CategoryFilter,
  Record<LamaHierarchyView, string>
> = {
  all: {
    baseline: "All categories",
    responses: "All categories",
    compare: "All categories",
  },
  assessed: {
    baseline: "Assessed in baseline",
    responses: "With responses",
    compare: "Comparable only",
  },
  unassessed: {
    baseline: "Not assessed",
    responses: "No responses",
    compare: "Not comparable",
  },
};

function Absent({ reason }: { reason: string }): ReactElement {
  return (
    <span className="text-pb-text-muted">
      <span aria-hidden="true">—</span>
      <span className="sr-only">{reason}</span>
    </span>
  );
}

function Figure({
  value,
  absent,
}: {
  value: number | null;
  absent: string;
}): ReactElement {
  return value === null ? (
    <Absent reason={absent} />
  ) : (
    <span className="pb-tabular">{lamaPercentLabel(value)}</span>
  );
}

function Change({
  delta,
  subject,
}: {
  delta: number | null;
  subject: string;
}): ReactElement {
  return delta === null ? (
    <Absent reason="Not comparable" />
  ) : (
    <TrendIndicator
      delta={Math.round(delta)}
      unit=" pts"
      aria-label={`${subject}: ${lamaChangeDescription(delta)}`}
    />
  );
}

function QuestionRows({
  view,
  questions,
}: {
  view: LamaHierarchyView;
  questions: readonly HierarchyQuestionRow[];
}): ReactElement {
  return (
    // eslint-disable-next-line ds/no-hand-built -- kept: a borderless results table inside the section; DataTable draws its own frame, header band and cell padding, and no approved look row lists the change.
    <table className="w-full text-sm">
      <caption className="sr-only">Question results</caption>
      <thead>
        <tr className="text-xs text-pb-text-muted">
          <th scope="col" className="py-1 text-start font-normal">
            Question
          </th>
          {view !== "responses" ? (
            <th scope="col" className="py-1 text-end font-normal">
              Baseline
            </th>
          ) : null}
          {view !== "baseline" ? (
            <th scope="col" className="py-1 text-end font-normal">
              Responses
            </th>
          ) : null}
          {view === "compare" ? (
            <th scope="col" className="py-1 text-end font-normal">
              Change
            </th>
          ) : null}
        </tr>
      </thead>
      <tbody>
        {questions.map((question) => (
          <tr key={question.id} className="border-t border-pb-border">
            <th
              scope="row"
              className="py-1.5 pe-3 text-start font-normal text-pb-text"
            >
              {question.prompt}
            </th>
            {view !== "responses" ? (
              <td className="py-1.5 text-end">
                <Figure value={question.baseline} absent="Not assessed" />
              </td>
            ) : null}
            {view !== "baseline" ? (
              <td className="py-1.5 text-end">
                <Figure value={question.responses} absent="Not assessed" />
              </td>
            ) : null}
            {view === "compare" ? (
              <td className="py-1.5 text-end">
                <Change delta={question.delta} subject={question.prompt} />
              </td>
            ) : null}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function CategoryRow({
  view,
  row,
  open,
  onToggle,
  panelId,
}: {
  view: LamaHierarchyView;
  row: HierarchyCategoryRow;
  open: boolean;
  onToggle: () => void;
  panelId: string;
}): ReactElement {
  return (
    <li className="border-t border-pb-border">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 py-2">
        {/* eslint-disable-next-line ds/no-hand-built -- kept: the category row's disclosure trigger with a turning chevron and two text lines; no DS trigger renders this row as it is, and no approved look row lists the change. */}
        <button
          type="button"
          className="flex min-w-40 flex-1 items-center gap-2 rounded-sm text-start focus-ring"
          aria-expanded={open}
          aria-controls={panelId}
          onClick={onToggle}
        >
          <ChevronRight
            aria-hidden="true"
            className={`h-4 w-4 shrink-0 text-pb-text-muted transition-transform ${
              open ? "rotate-90" : ""
            }`}
          />
          <span>
            <span className="block text-sm font-medium text-pb-text">
              {row.label}
            </span>
            <span className="block text-xs text-pb-text-muted">
              {row.detail}
            </span>
          </span>
        </button>
        <div className="flex items-center gap-4 text-sm">
          {view !== "responses" ? (
            <span className="w-14 text-end">
              <span className="sr-only">Baseline </span>
              <Figure value={row.baseline} absent="Not assessed" />
            </span>
          ) : null}
          {view !== "baseline" ? (
            <span className="w-14 text-end">
              <span className="sr-only">Responses </span>
              <Figure value={row.responses} absent="Not assessed" />
            </span>
          ) : null}
          {view === "compare" ? (
            <span className="w-20 text-end">
              <Change delta={row.delta} subject={row.label} />
            </span>
          ) : null}
        </div>
      </div>
      {open ? (
        <div
          id={panelId}
          className="mb-3 ms-6 rounded-md bg-pb-card-elevated/40 px-3 py-2"
        >
          {row.questions.length > 0 ? (
            <QuestionRows view={view} questions={row.questions} />
          ) : null}
          {row.questionNote ? (
            <p className="mt-1 text-xs text-pb-text-muted">
              {row.questionNote}
            </p>
          ) : null}
        </div>
      ) : (
        <div id={panelId} hidden />
      )}
    </li>
  );
}

/**
 * LAMA Results by Pillar and Category (2026-09-10 ADR-0027 amendment) — the
 * drill-down: every pillar as a section, every category as an expandable row
 * that opens to its questions. Data-blind over `lama/maturity-hierarchy@1`
 * plus the host's display-only `view`; nothing here re-averages a figure.
 *
 * A category-level baseline judgement has NO question rows — nothing per
 * question was ever entered — and the panel says so instead of inventing them.
 * In Compare only the questions assessed on BOTH sides are listed, with a note
 * naming how many were left out.
 */
export function LamaHierarchyResultsWidget({
  data,
}: WidgetBodyProps<LamaMaturityHierarchyView>): ReactElement {
  const view = effectiveHierarchyView(data);
  const missing = hierarchyMissingReason(data, view);
  const sections = hierarchySections(data, view);
  const [filter, setFilter] = useState<CategoryFilter>("all");
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());
  const idBase = useId();

  const toggle = (categoryId: string): void =>
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(categoryId)) next.delete(categoryId);
      else next.add(categoryId);
      return next;
    });
  const keep = (row: HierarchyCategoryRow): boolean =>
    filter === "all"
      ? true
      : filter === "assessed"
        ? row.assessed
        : !row.assessed;

  if (missing === "suppressed") {
    return (
      <div>
        <p className="text-sm font-medium text-pb-text">
          {LAMA_SUPPRESSED_TITLE}
        </p>
        <p className="mt-1 text-sm text-pb-text-muted">
          {LAMA_SUPPRESSED_BODY}
        </p>
      </div>
    );
  }
  if (missing === "no-baseline" || missing === "no-responses") {
    return (
      <p className="text-sm text-pb-text-muted">
        {missing === "no-baseline"
          ? LAMA_NO_BASELINE_COPY
          : LAMA_NO_RESPONSES_COPY}
      </p>
    );
  }

  const total = sections.reduce(
    (count, section) => count + section.categories.length,
    0,
  );
  const shown = sections.reduce(
    (count, section) => count + section.categories.filter(keep).length,
    0,
  );

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-2">
        {/* Provenance renders ONCE, on the report's view-switch card — see the
            note in `lamaMaturityHierarchy`. This section keeps only which view
            it drew, because a fallback makes that differ from the switch. */}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-pb-text-muted">
          <span data-testid="lama-hierarchy-view">
            Viewing:{" "}
            <span className="font-medium text-pb-text">
              {LAMA_HIERARCHY_VIEW_LABEL[view]}
            </span>
          </span>
        </div>
        {/* The design system's `Select`, never a bare `<select>`: a native
            control ignores the token palette and renders as the operating
            system's own widget in the middle of a themed report. The options
            name themselves ("All categories", "Comparable only"), so the
            control needs no visible "Show" beside it — only an accessible
            name. */}
        <Select
          ariaLabel="Filter categories"
          className="w-48 shrink-0"
          value={filter}
          onChange={(next) => setFilter(next as CategoryFilter)}
          options={CATEGORY_FILTERS.map((option) => ({
            value: option,
            label: FILTER_LABEL[option][view],
          }))}
        />
      </div>
      <p className="mt-1 text-xs text-pb-text-muted" role="status">
        {filter === "all"
          ? `${total} ${total === 1 ? "category" : "categories"}`
          : `${shown} of ${total} categories`}
      </p>

      {sections.map((section) => {
        const categories = section.categories.filter(keep);
        if (categories.length === 0) return null;
        const headingId = `${idBase}-${section.id}-heading`;
        return (
          <section
            key={section.id}
            aria-labelledby={headingId}
            className="mt-4"
          >
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <h3 id={headingId} className="text-sm font-semibold text-pb-text">
                {section.label}
              </h3>
              <div className="flex items-center gap-4 text-sm">
                {view !== "responses" && section.baseline !== null ? (
                  <span>
                    <span className="text-xs text-pb-text-muted">
                      Baseline{" "}
                    </span>
                    <span className="pb-tabular">
                      {lamaPercentLabel(section.baseline)}
                    </span>
                  </span>
                ) : null}
                {view !== "baseline" && section.responses !== null ? (
                  <span>
                    <span className="text-xs text-pb-text-muted">
                      Responses{" "}
                    </span>
                    <span className="pb-tabular">
                      {lamaPercentLabel(section.responses)}
                    </span>
                  </span>
                ) : null}
                {view === "compare" && section.delta !== null ? (
                  <TrendIndicator
                    delta={Math.round(section.delta)}
                    unit=" pts"
                    aria-label={`${section.label}: ${lamaChangeDescription(section.delta)}`}
                  />
                ) : null}
                <span className="text-xs text-pb-text-muted">
                  {section.coverage}
                </span>
              </div>
            </div>
            <ul className="mt-1">
              {categories.map((row) => (
                <CategoryRow
                  key={row.id}
                  view={view}
                  row={row}
                  open={expanded.has(row.id)}
                  onToggle={() => toggle(row.id)}
                  panelId={`${idBase}-${row.id}-questions`}
                />
              ))}
            </ul>
          </section>
        );
      })}
      <p className="mt-3 text-xs text-pb-text-muted">
        Expand a category to inspect its questions.{" "}
        {view === "compare"
          ? "Only questions assessed on both sides are compared; category-level baseline entries have no question breakdown."
          : "Category-level baseline entries have no question breakdown."}
      </p>
    </div>
  );
}
